import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/solid';
import { ScrapingStatus, RaceEvent, ScrapingSource } from '../../types/scraping';
import { FormField } from '../../components/common/FormField';
import { api } from '../../services/api';
import { Modal } from '../../components/common/Modal';

const ScrapingManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Fetch scraping status
  const { data: status, isLoading: isStatusLoading } = useQuery<ScrapingStatus>({
    queryKey: ['scrapingStatus'],
    queryFn: () => api.admin.scraping.getStatus()
  });

  // Fetch recent events
  const { data: recentEvents, isLoading: isEventsLoading } = useQuery<RaceEvent[]>({
    queryKey: ['recentEvents'],
    queryFn: () => api.admin.scraping.getRecentEvents()
  });

  // Fetch scraping configuration
  const { data: scrapingConfig, isLoading: isConfigLoading } = useQuery({
    queryKey: ['scrapingConfig'],
    queryFn: () => api.admin.scraping.getConfig()
  });

  // Start scraping mutation
  const startScraping = useMutation({
    mutationFn: () => api.admin.scraping.start(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrapingStatus'] });
    }
  });

  // Stop scraping mutation
  const stopScraping = useMutation({
    mutationFn: () => api.admin.scraping.stop(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrapingStatus'] });
    }
  });

  // Update config mutation
  const updateConfig = useMutation({
    mutationFn: (config: any) => api.admin.scraping.updateConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scrapingConfig'] });
      setShowConfigModal(false);
    }
  });

  // Refresh status mutation
  const refreshStatus = useMutation({
    mutationFn: () => api.admin.scraping.getStatus(),
    onSuccess: (data) => {
      queryClient.setQueryData(['scrapingStatus'], data);
    }
  });

  if (isStatusLoading || isEventsLoading || isConfigLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-deep"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-teal-300">Race Data Scraping</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowConfigModal(true)}
            className="inline-flex items-center px-4 py-2 bg-black/30 border border-teal-500/20 text-teal-300 rounded-md hover:bg-black/40 transition-colors"
          >
            <Cog6ToothIcon className="h-5 w-5 mr-2" />
            Configure
          </button>
          {status?.isRunning ? (
            <button
              onClick={() => stopScraping.mutate()}
              className="inline-flex items-center px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5 mr-2" />
              Stop Scraping
            </button>
          ) : (
            <button
              onClick={() => startScraping.mutate()}
              className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Start Scraping
            </button>
          )}
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 p-6">
        <h2 className="text-lg font-medium text-teal-300 mb-4">Scraping Status</h2>
        {isStatusLoading ? (
          <div className="flex items-center justify-center h-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
          </div>
        ) : status ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-black/30 rounded-lg p-4">
              <div className="flex items-center">
                <div className={`h-3 w-3 rounded-full mr-2 ${status.isRunning ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                <span className="text-gray-200">Status</span>
              </div>
              <p className="mt-2 text-lg font-semibold text-teal-300">
                {status.isRunning ? 'Running' : 'Stopped'}
              </p>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-gray-200">Last Run</span>
              </div>
              <p className="mt-2 text-lg font-semibold text-teal-300">
                {status.lastRun ? new Date(status.lastRun).toLocaleString() : 'Never'}
              </p>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                <span className="text-gray-200">Success Rate</span>
              </div>
              <p className="mt-2 text-lg font-semibold text-teal-300">
                {status.successRate}%
              </p>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <div className="flex items-center">
                <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                <span className="text-gray-200">Errors</span>
              </div>
              <p className="mt-2 text-lg font-semibold text-teal-300">
                {status.errors}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-red-400">Failed to load status</div>
        )}
      </div>

      {/* Recent Events */}
      <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 p-6">
        <h2 className="text-lg font-medium text-teal-300 mb-4">Recent Events</h2>
        {isEventsLoading ? (
          <div className="flex items-center justify-center h-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400"></div>
          </div>
        ) : recentEvents?.length ? (
          <div className="space-y-4">
            {recentEvents.map((event, index) => (
              <div key={index} className="bg-black/30 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-gray-200 font-medium">{event.title}</h3>
                    <p className="text-gray-400 mt-1">{event.description}</p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(event.timestamp).toLocaleString()}
                  </span>
                </div>
                {event.error && (
                  <div className="mt-2 text-sm text-red-400">
                    <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                    {event.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-gray-400">No recent events</div>
        )}
      </div>

      {/* Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Scraping Configuration"
      >
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-teal-300">Update Interval (minutes)</label>
              <input
                type="number"
                value={scrapingConfig?.updateInterval || 0}
                onChange={(e) => updateConfig.mutate({ ...scrapingConfig, updateInterval: parseInt(e.target.value) })}
                className="mt-1 block w-full bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-teal-300">Sources</label>
              <div className="mt-2 space-y-2">
                {scrapingConfig?.sources.map((source: ScrapingSource, index: number) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={source.url}
                      onChange={(e) => {
                        const newSources = [...scrapingConfig.sources];
                        newSources[index] = { ...source, url: e.target.value };
                        updateConfig.mutate({ ...scrapingConfig, sources: newSources });
                      }}
                      className="flex-1 bg-black/30 border-gray-600 text-gray-200 rounded-md focus:ring-teal-500 focus:border-teal-500"
                    />
                    <button
                      onClick={() => {
                        const newSources = scrapingConfig.sources.filter((_: ScrapingSource, i: number) => i !== index);
                        updateConfig.mutate({ ...scrapingConfig, sources: newSources });
                      }}
                      className="p-2 text-red-400 hover:text-red-300"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newSources = [...scrapingConfig.sources, { url: '', type: 'race' }];
                    updateConfig.mutate({ ...scrapingConfig, sources: newSources });
                  }}
                  className="inline-flex items-center px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-teal-600 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Source
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ScrapingManagementPage; 